import React, { useMemo, useState } from 'react';
import { SafeAreaView, ScrollView, View, Text, TextInput, TouchableOpacity, StyleSheet } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../i18n';
import { PlatformIcon as Icon } from '@cubemeet/ui';
import { LocalizedAuthService } from '@cubemeet/core/services/localizedAuthService';

const ForgotPasswordPage: React.FC = () => {
  const { translations } = useI18n();
  const t = useMemo(() => {
    return (key: string, params?: Record<string, any>): string => {
      if (!translations) return key;
      const value = key.split('.').reduce<any>((acc, part) => (acc && typeof acc === 'object' ? acc[part] : null), translations);
      if (typeof value === 'string') {
        if (!params) return value;
        return value.replace(/\{(\w+)\}/g, (_, p) => String(params[p] ?? ''));
      }
      return key;
    };
  }, [translations]);
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const validateEmail = (value: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!value) {
      setError(t('auth.validation.emailRequired'));
      return false;
    }
    if (!emailRegex.test(value)) {
      setError(t('auth.validation.emailInvalid'));
      return false;
    }
    setError(null);
    return true;
  };

  const handleReset = async () => {
    if (!validateEmail(email)) return;
    setLoading(true);
    try {
      await LocalizedAuthService.resetPasswordWithLocalizedEmail({ email: email.trim() });
      // Show success state instead of immediate navigation to give clear feedback
      setSent(true);
    } catch (err: any) {
      console.error('重置密碼失敗', err);
      setError(err?.message || String(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <ScrollView contentContainerStyle={styles.content} keyboardShouldPersistTaps="handled">
        <View style={styles.header}>
          <TouchableOpacity style={styles.back} onPress={() => navigate('/auth')}>
            <Icon name="arrow-back" size={22} color="#2563EB" />
            <Text style={styles.backText}>{t('common.back') || '返回'}</Text>
          </TouchableOpacity>
        </View>

        <View style={styles.form}>
          <Text style={styles.title}>{t('auth.resetPassword')}</Text>

          <Text style={styles.label}>{t('auth.email')}</Text>
          <TextInput style={styles.input} value={email} onChangeText={setEmail} placeholder={t('auth.emailPlaceholder')} keyboardType="email-address" />

          {error ? <Text style={styles.error}>{error}</Text> : null}

          {!sent ? (
            <TouchableOpacity style={[styles.button, loading && styles.buttonDisabled]} onPress={handleReset} disabled={loading}>
              <Text style={styles.buttonText}>{t('auth.sendResetEmail')}</Text>
            </TouchableOpacity>
          ) : (
            <View style={styles.successBox}>
              <Text style={styles.successText}>
                {t('auth.resetEmailSentMessage') || '重置郵件已發送，請到郵箱查收並按提示設置新密碼。'}
              </Text>
            </View>
          )}

          <View style={styles.rowCenter}>
            <Text>{t('auth.rememberPassword') || '还记得密码？'}</Text>
            <TouchableOpacity onPress={() => navigate('/auth')}>
              <Text style={styles.link}>{t('auth.backToLogin')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: '#f9f9f9' },
  content: { flexGrow: 1, padding: 20 },
  header: { height: 48 },
  back: { flexDirection: 'row', alignItems: 'center' },
  backText: { color: '#2563EB', marginLeft: 6 },
  form: { maxWidth: 540, alignSelf: 'center' },
  title: { fontSize: 24, fontWeight: '700', color: '#111827', marginBottom: 16 },
  label: { fontSize: 14, color: '#6B7280', marginTop: 12, marginBottom: 6 },
  input: { backgroundColor: '#fff', borderRadius: 8, borderWidth: 1, borderColor: '#E5E7EB', paddingHorizontal: 12, paddingVertical: 10 },
  button: { marginTop: 20, backgroundColor: '#2563EB', paddingVertical: 12, borderRadius: 8, alignItems: 'center' },
  buttonDisabled: { opacity: 0.6 },
  buttonText: { color: '#fff', fontWeight: '700' },
  link: { color: '#2563EB', marginLeft: 8 },
  rowCenter: { flexDirection: 'row', justifyContent: 'center', marginTop: 12, alignItems: 'center' },
  error: { color: '#B91C1C', marginTop: 10 },
  successBox: { marginTop: 16, backgroundColor: '#ECFDF5', borderColor: '#A7F3D0', borderWidth: 1, padding: 12, borderRadius: 8 },
  successText: { color: '#065F46' },
});

export default ForgotPasswordPage;
